import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../axios.js';
import type { FrequencyRange } from '../types/frequency-range';
import type { FrequencyWhitelist } from '../types/frequency-whitelist';

const getDeviceWhitelist = async (id: string): Promise<FrequencyWhitelist | null> => {
  try {
    const { data } = await apiClient.get<FrequencyWhitelist | null>(`/devices/${id}/whitelist`);
    return data;
  } catch (error) {
    Sentry.captureException(error);
    console.debug({ error });
    throw new Error('Failed to fetch device whitelist');
  }
};

const setDeviceWhitelist = async (params: {
  id: string;
  entries: FrequencyRange[];
}): Promise<FrequencyWhitelist> => {
  const { id, entries } = params;
  const { data } = await apiClient.post<FrequencyWhitelist>(`/devices/${id}/whitelist`, {
    entries,
  });
  return data;
};

export const useDeviceWhitelist = (id: string | null | undefined) => {
  return useQuery<FrequencyWhitelist | null>({
    queryKey: ['device', 'whitelist', id],
    queryFn: () => getDeviceWhitelist(id as string),
    enabled: !!id,
    staleTime: 60_000,
  });
};

export const useSetDeviceWhitelist = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setDeviceWhitelist,
    onSuccess: (_res, vars) => {
      // refresh cached whitelist for the device
      queryClient.invalidateQueries({ queryKey: ['device', 'whitelist', vars.id] });
    },
  });
};
